/**
 * JobCardSmsButton
 * ────────────────
 * Inline SMS quick-send button for the Job Card detail modal. SM/SA only.
 *
 * Workflow:
 *   1. Button shows count of templates available; clicks open a popover.
 *   2. SM picks an SMS template OR types a custom message.
 *   3. Live preview is rendered server-side against the JC's customer +
 *      vehicle + workshop context so the SM sees the EXACT final text.
 *   4. "Send" hits `/api/sms/jobcard/{id}/send` — message is logged to
 *      sms_outbox (dry-run if no CELLCAST_APPKEY, otherwise fired live).
 *
 * Mechanics never see this button — gated by `user.role` upstream.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Phone, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCommsGate } from '@/components/CommsUpgradeDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobCardSmsButton({ jobCard, user }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [bodyOverride, setBodyOverride] = useState('');
  const [preview, setPreview] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [keyConfigured, setKeyConfigured] = useState(true);
  // iter124: full customer doc so we can show landline alongside mobile
  // in the multi-recipient picker (parity with Invoice + Booking SMS).
  const [customer, setCustomer] = useState(null);
  const [smsTargets, setSmsTargets] = useState({ mobile: true, landline: false });
  const { guardComms, commsDialog } = useCommsGate();

  const canUse = useMemo(
    () => user && ['super_admin', 'service_manager', 'floor_manager'].includes(user.role),
    [user],
  );

  // Convenience getters — pull mobile / landline from BOTH the customer
  // doc (canonical) and the JC snapshot (fallback for older JCs).
  const customerMobile =
    customer?.mobile ||
    jobCard?.customer_mobile ||
    jobCard?.customer_phone ||
    null;
  const customerLandline =
    customer?.phone ||
    customer?.landline ||
    jobCard?.customer_landline ||
    null;
  // Hide landline when it's the same number as mobile (some legacy
  // customer rows have both columns populated with the same value).
  const showLandline = customerLandline && customerLandline !== customerMobile;

  // Fetch SMS templates + key-configured flag (via outbox endpoint) when opening.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        // iter121: use the canonical template-library endpoint that's
        // populated by Super Admin → Templates → SMS tab. The legacy
        // `/api/templates?template_type=sms` ignores `template_type` so
        // it returned the wrong rows (or none at all).
        const r = await fetch(
          `${BACKEND_URL}/api/template-library?template_type=sms`,
          { credentials: 'include' },
        );
        if (r.ok && !cancelled) {
          const data = await r.json();
          setTemplates(Array.isArray(data) ? data : (data.templates || []));
        }
        const ob = await fetch(`${BACKEND_URL}/api/sms/outbox?limit=1`, { credentials: 'include' });
        if (ob.ok && !cancelled) {
          const d = await ob.json();
          setKeyConfigured(!!d.key_configured);
        }
        // iter124: fetch the full customer doc so we can show landline
        // alongside mobile in the recipient checkbox UI.
        if (jobCard?.customer_id) {
          const cr = await fetch(`${BACKEND_URL}/api/customers/${jobCard.customer_id}`, { credentials: 'include' });
          if (cr.ok && !cancelled) {
            const c = await cr.json();
            setCustomer(c);
            // Default: mobile checked when present; landline auto-checked
            // only when no mobile is available.
            const mob = c?.mobile || jobCard?.customer_phone;
            const land = c?.phone || c?.landline;
            setSmsTargets({ mobile: !!mob, landline: !!land && !mob });
          }
        }
      } catch (_e) { /* noop */ }
    })();
    // Pre-fill body with the Mechanic Findings "Additional free-form
    // notes" textarea — the SM most often wants to SMS this content
    // verbatim as a quick quote-over-the-phone follow-up.
    if (jobCard?.findings && !bodyOverride) {
      setBodyOverride(jobCard.findings);
    }
    return () => { cancelled = true; };
  }, [open]);

  // Re-render preview whenever template / body changes.
  useEffect(() => {
    if (!open) return;
    if (!templateId && !bodyOverride.trim()) {
      setPreview('');
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/sms/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            template_id: bodyOverride ? null : templateId,
            body: bodyOverride || null,
            job_card_id: jobCard?.job_card_id,
          }),
        });
        if (r.ok && !cancelled) {
          const d = await r.json();
          setPreview(d.rendered || '');
        }
      } catch (_e) { /* noop */ }
      finally { if (!cancelled) setPreviewing(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, templateId, bodyOverride, jobCard?.job_card_id]);

  const reset = () => {
    setTemplateId(''); setBodyOverride(''); setPreview('');
  };

  const handleSend = async () => {
    if (!preview.trim() && !bodyOverride.trim()) {
      toast.error('Pick a template or type a message first');
      return;
    }
    // iter124: build target list from the multi-recipient picker.
    const targets = [];
    if (smsTargets.mobile && customerMobile) targets.push(customerMobile);
    if (smsTargets.landline && customerLandline && customerLandline !== customerMobile) targets.push(customerLandline);
    if (targets.length === 0) {
      toast.error('Tick at least one number (mobile or landline) to send to');
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sms/jobcard/${jobCard.job_card_id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_id: bodyOverride ? null : templateId || null,
          body_override: bodyOverride || null,
          targets,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        const status = data?.outbox?.status;
        const recipientCount = data?.recipients ?? targets.length;
        const recipientLabel = recipientCount > 1 ? `${recipientCount} numbers` : (targets[0] || '');
        if (status === 'dry_run') {
          toast.success(`SMS recorded (dry-run) for ${recipientLabel} — no CELLCAST key configured`);
        } else if (status === 'sent') {
          toast.success(`SMS sent ✓ to ${recipientLabel}`);
        } else if (status === 'failed') {
          toast.error(`Send failed: ${data.outbox?.error || 'unknown'}`);
        } else {
          toast.success(`SMS queued for ${recipientLabel}`);
        }
        setOpen(false); reset();
      } else {
        toast.error(data.detail || 'Failed to send SMS');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally { setSending(false); }
  };

  if (!canUse) return null;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={guardComms(() => setOpen(true))}
        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        data-testid="jc-sms-button"
        title={customerMobile ? `Send SMS to ${customerMobile}` : 'Customer has no mobile on file'}
        disabled={!customerMobile}
      >
        <Phone className="h-3.5 w-3.5 mr-1" /> SMS
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="jc-sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-emerald-600" />
              Send SMS — {jobCard?.customer_name || 'customer'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {!keyConfigured && (
              <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="jc-sms-dryrun-notice">
                Dry-run mode — CellCast key not configured. Your message will be recorded in the outbox so you can verify content; nothing is actually delivered until <code>CELLCAST_APPKEY</code> is set in <code>backend/.env</code>.
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-600">Send to</Label>
              {/* iter124: multi-recipient picker — same UX as Invoice
                  and Booking SMS modals. Pulls mobile + landline from
                  the customer doc; SM ticks one or both. */}
              <div className="mt-1.5 space-y-1.5" data-testid="jc-sms-targets">
                {customerMobile ? (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="jc-sms-target-mobile">
                    <input
                      type="checkbox"
                      checked={smsTargets.mobile}
                      onChange={(e) => setSmsTargets(t => ({ ...t, mobile: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide w-20">Mobile</span>
                    <span className="font-mono text-xs text-slate-700">{customerMobile}</span>
                  </label>
                ) : (
                  <p className="text-[11px] text-slate-400 italic">No mobile on file.</p>
                )}
                {showLandline ? (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="jc-sms-target-landline">
                    <input
                      type="checkbox"
                      checked={smsTargets.landline}
                      onChange={(e) => setSmsTargets(t => ({ ...t, landline: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide w-20">Landline</span>
                    <span className="font-mono text-xs text-slate-700">{customerLandline}</span>
                  </label>
                ) : (
                  !customerMobile && <p className="text-[11px] text-rose-600 italic">⚠️ No phone numbers on file for this customer.</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">SMS Template</Label>
              <Select value={templateId} onValueChange={(v) => { setTemplateId(v); setBodyOverride(''); }}>
                <SelectTrigger className="mt-1 h-9" data-testid="jc-sms-template-select">
                  <SelectValue placeholder={templates.length === 0 ? 'No SMS templates yet — type below' : 'Pick a template…'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">
                Custom message <span className="text-slate-400">(overrides template)</span>
              </Label>
              <Textarea
                value={bodyOverride}
                onChange={(e) => setBodyOverride(e.target.value)}
                placeholder="Hi {{customer_first_name}}, your car {{vehicle_rego}} is ready for pickup at {{workshop_name}}. Cheers!"
                rows={3}
                className="mt-1 text-sm font-mono"
                data-testid="jc-sms-override-input"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-2">
                Live preview
                {previewing && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
              </Label>
              <div
                className="mt-1 min-h-[60px] rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap"
                data-testid="jc-sms-preview"
              >
                {preview || <span className="text-slate-400">Pick a template or type a message to see the rendered output…</span>}
              </div>
              {preview && (
                <p className="text-[10px] text-slate-500 mt-0.5" data-testid="jc-sms-charcount">
                  {preview.length} chars · {Math.ceil(preview.length / 160) || 1} SMS segment{Math.ceil(preview.length / 160) > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }} data-testid="jc-sms-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !customerMobile || (!templateId && !bodyOverride.trim())}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="jc-sms-send"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              {keyConfigured ? 'Send SMS' : 'Record (dry-run)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {commsDialog}
    </>
  );
}
